import { useState, useEffect, useRef } from "react";
import "../../../assets/css/marketingEfficiencyIndex.scss";
import { useLocalStorage } from "../../../contexts/LocalStorageContext";
import { fetchPeerSets } from "../../../shared/utils/fetchPeerSets";
import {
  Box,
  Chip,
  Container,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  linearProgressClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
// import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DateRangePicker, {
  DateRangeData,
} from "../../../shared/components/dateRangePicker";
import { QuickPresetsSelect } from "../../../shared/components/quickPresetsSelect";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { CircularProgress } from "@mui/material";
import { BenchMarkErrorType, BenchmarkScreenType } from "../../../shared/enum";
import BenchmarkErrorPage from "../benchMarkError";
import PrintContainer from "../../../shared/components/printContainer";
import { ReportExportActions } from "../../../shared/components/reportExportButton";

// ─── Types ──────────────────────────────────────────────────────────────────────

interface DashboardData {
  filters: { dateRange: string; market: string };
  meiScore: {
    value: number;
    vspeers: string;
    peerAvg: number;
    percentile: string;
    percentileValue: number;
  };
  opportunities: { severity: "red" | "yellow" | "green"; text: string }[];
  metrics: {
    label: string;
    weight: string;
    score: number;
    vsPeers: string;
    trend: "up" | "down";
    status: "green" | "yellow" | "red";
  }[];
  meiTrend: { month: number; property: number; peers: number }[];
  peerDistribution: { label: string; percentile: number; color: string }[];
}

// ─── Theme tokens ──────────────────────────────────────────────────────────────

const DARK_BG = "#1e2d24";

const oppSeverityColor: Record<string, string> = {
  green: "#528A4D",
  yellow: "#AC782B",
  red: "#BA4143",
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

const ordinal = (n: number) => {
  if ([11, 12, 13].includes(n % 100)) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
};

// ─── Styled components ─────────────────────────────────────────────────────────

const DarkPaper = styled(Paper)({
  backgroundColor: DARK_BG,
  color: "#fff",
  borderRadius: 10,
  padding: "24px 28px",
});

const StyledProgress = styled(LinearProgress)<{ barcolor: string }>(
  ({ barcolor }) => ({
    height: 10,
    borderRadius: 50,
    [`&.${linearProgressClasses.colorPrimary}`]: { backgroundColor: "#f0f0ec" },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 50,
      backgroundColor: barcolor,
      boxShadow: "0px 4px 14.4px rgba(0,0,0,0.10)",
    },
  }),
);

// ─── Main Component ────────────────────────────────────────────────────────────

const MarketingEfficiencyIndexReport = () => {
  const reportRef = useRef<HTMLDivElement>(null);
  const [trendPeriod, setTrendPeriod] = useState<"6" | "12">("6");
  const [cohortPropertyCount, setCohortPropertyCount] = useState(0);
  const [errorType, setErrorType] = useState<BenchMarkErrorType>(
    BenchMarkErrorType.INVALID_FILTER,
  );

  const [dateRange, setDateRange] = useState<DateRangeData>(() => {
    const today = new Date();

    const pastDate = new Date();
    pastDate.setDate(today.getDate() - 29);

    return {
      from: {
        year: pastDate.getFullYear(),
        month: pastDate.getMonth() + 1,
        day: pastDate.getDate(),
      },
      to: {
        year: today.getFullYear(),
        month: today.getMonth() + 1,
        day: today.getDate(),
      },
    };
  });
  const [loading, setLoading] = useState(false);
  const [selectedPeerSet, setSelectedPeerSet] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [peerSets, setPeerSets] = useState<
    { id: string; title: string; subtitles: string[] }[]
  >([]);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null,
  );
  const LAYER_BASE_URL = process.env.REACT_APP_API_LAYER_URL;
  const { propertyId, propertyTitle } = useLocalStorage();

  const handleDateChange = (v: DateRangeData) => setDateRange(v);
  const handleResetFilter = () => {};

  useEffect(() => {
    const loadPeerSets = async () => {
      if (!propertyId) return;
      setLoading(true);
      const { peerSets: fetched, defaultPeerSet } =
        await fetchPeerSets(propertyId);
      setPeerSets(fetched);
      if (defaultPeerSet) {
        setSelectedPeerSet({
          id: defaultPeerSet.id,
          title: defaultPeerSet.title,
        });
      }
      setLoading(false);
    };
    loadPeerSets();
  }, [propertyId]);

  const fetchMarketingEfficiencyIndex = async () => {
    if (!dateRange?.from || !dateRange?.to || !selectedPeerSet?.id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        sourcePropertyId: String(propertyId),
        startDate: `${dateRange.from.year}-${String(dateRange.from.month).padStart(2, "0")}-${String(dateRange.from.day).padStart(2, "0")}`,
        endDate: `${dateRange.to.year}-${String(dateRange.to.month).padStart(2, "0")}-${String(dateRange.to.day).padStart(2, "0")}`,
        peerSetId: selectedPeerSet.id,
        trendPeriod: trendPeriod,
      });
      const response = await fetch(
        `${LAYER_BASE_URL}/benchmarking-reports/marketing-efficiency-index?${params.toString()}`,
      );
      const data = await response.json();
      if (!response.ok || data?.error) {
        const count = data?.error?.details?.cohort_property_count ?? 0;
        const code = data?.error?.code;
        setCohortPropertyCount(count);
        setErrorType(
          code === "INSUFFICIENT_COHORT"
            ? BenchMarkErrorType.NOT_ENOUGH_PEER
            : BenchMarkErrorType.INVALID_FILTER,
        );
        setDashboardData(null);
        return;
      }

      setCohortPropertyCount(0);
      setErrorType(BenchMarkErrorType.INVALID_FILTER);
      setDashboardData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarketingEfficiencyIndex();
  }, [propertyId, dateRange, selectedPeerSet, trendPeriod]);

  const {
    filters,
    meiScore,
    opportunities,
    metrics,
    meiTrend,
    peerDistribution,
  } = dashboardData ?? {};

  const trendData = meiTrend ?? [];

  const generateMEICSV = () => {
    const headers = [
      "Section",
      "Label",
      "Value",
      "Peer Reference",
      "Status/Trend",
    ];
    const rows: any[][] = [];

    // 1. MEI Score Summary
    rows.push([
      "Summary",
      "MEI Score",
      meiScore?.value,
      `${meiScore?.vspeers} vs Peers`,
      meiScore?.percentile,
    ]);
    rows.push(["Summary", "Peer Average", meiScore?.peerAvg, "", ""]);
    rows.push([]);

    // 2. Opportunities
    opportunities?.forEach((op) => {
      rows.push(["Opportunity", "Improvement Tip", op.text, "", op.severity]);
    });
    rows.push([]);

    // 3. Detailed Metrics
    rows.push(["Metrics", "Metric Name", "Score", "vs Peers", "Weight"]);
    metrics?.forEach((m) => {
      rows.push(["Metrics", m.label, m.score, m.vsPeers, m.weight]);
    });
    rows.push([]);

    // 4. Trend Data
    rows.push(["Trend", "Month", "Property Score", "Peer Median", ""]);
    meiTrend?.forEach((t) => {
      rows.push(["Trend", t.month, t.property, t.peers, ""]);
    });
    rows.push([]);

    // 5. Peer Distribution
    peerDistribution?.forEach((pd) => {
      rows.push([
        "Distribution",
        pd.label,
        `${pd.percentile}%`,
        "Percentile",
        "",
      ]);
    });

    return { headers, rows };
  };

  const { headers: csvHeaders, rows: csvRows } = generateMEICSV();

  return (
    <Box
      sx={{ bgcolor: "#f5f4f0", minHeight: "100vh", py: 4, px: 5 }}
      className="benchmarks-report-root"
      ref={reportRef}
    >
      <PrintContainer
        title="Marketing Efficiency Index Report"
        propertyTitle={propertyTitle}
      >
        <Container maxWidth={false} disableGutters>
          {/* ── Header ── */}
          <Stack className="mei-header">
            <Typography className="mei-heading">
              Marketing Efficiency Index
            </Typography>
          </Stack>

          {/* ── Filters + Export ── */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
            className="no-print"
          >
            <Stack direction="row" gap="12px" alignItems="center">
              <Box className="control-group">
                <span style={{ marginTop: "1px" }} className="date-range-label">
                  Date Range:
                </span>
                <DateRangePicker
                  dateRange={dateRange}
                  onChange={handleDateChange}
                  setDateRange={setDateRange}
                  setFilterData={handleResetFilter}
                  inputClassName="dateRangePickerInput"
                />
              </Box>
              <Box className="control-group" mt={1}>
                <span className="date-range-label">Peer Set:</span>
                <QuickPresetsSelect
                  selectedId={selectedPeerSet?.id}
                  presets={peerSets}
                  onSelect={(preset) => setSelectedPeerSet(preset)}
                  placeholder={selectedPeerSet?.title}
                />
              </Box>
            </Stack>
            <ReportExportActions
              filename="Marketing_Efficiency_Index"
              csvHeaders={csvHeaders}
              csvData={csvRows}
              componentRef={reportRef}
              loading={loading}
            />
          </Stack>

          {loading && (
            <Paper className="card-panel matrix-card" elevation={0}>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="70vh"
              >
                <CircularProgress />
              </Box>
            </Paper>
          )}

          {!loading && !dashboardData && (
            <BenchmarkErrorPage
              currentPeerCount={cohortPropertyCount}
              errorType={errorType}
              screenType={BenchmarkScreenType.MEI}
            />
          )}

          {!loading && dashboardData && (
            <Box className="report-content-wrapper">
              <>
                {/* ── Top Row: Score + Opportunities ── */}
                <Grid container spacing={2} mb={2}>
                  {/* MEI Score */}
                  <Grid item xs={12} md={3.5}>
                    <DarkPaper className="mei-score-card">
                      <Typography className="mei-score-label">
                        MEI Score
                      </Typography>

                      <Typography className="mei-score-value">
                        {meiScore?.value}
                      </Typography>

                      <Chip
                        label={`${meiScore?.vspeers} vs Peers`}
                        className="mei-score-chip"
                        size="small"
                      />

                      <Typography className="mei-peer-avg">
                        Peer avg = {meiScore?.peerAvg}
                      </Typography>

                      <LinearProgress
                        className="mei-progress"
                        variant="determinate"
                        value={meiScore?.percentileValue}
                      />

                      <Typography className="mei-percentile">
                        {meiScore?.percentile}
                      </Typography>
                    </DarkPaper>
                  </Grid>

                  {/* Opportunities */}
                  <Grid item xs={12} md={8.5}>
                    <DarkPaper className="mei-opportunities-card ">
                      <Typography className="mei-opportunities-title">
                        Opportunities to Improve
                      </Typography>

                      {opportunities?.map((op, i) => (
                        <Box key={i}>
                          {i > 0 && (
                            <Box className="mei-opportunities-divider" />
                          )}
                          <Box className="mei-opportunities-item">
                            <Box
                              className="mei-opportunities-dot"
                              sx={{ bgcolor: oppSeverityColor[op.severity] }}
                            />
                            <Typography className="mei-opportunities-text">
                              {op.text}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </DarkPaper>
                  </Grid>
                </Grid>

                {/* ── Metric Cards ── */}
                <Grid
                  container
                  spacing={1.75}
                  mb={2}
                  mt="25px"
                  className="metrics-grid"
                >
                  {metrics?.map((m) => (
                    <Grid item xs={12} sm={6} md={12 / 5} key={m.label}>
                      <Box className={`metric-card metric-card--${m.status}`}>
                        <Typography className="metric-card__title">
                          {m.label}
                        </Typography>
                        <Typography className="metric-card__weight">
                          Weight: {m.weight}
                        </Typography>

                        <Typography className="metric-card__score">
                          {m.score}
                        </Typography>

                        <Box className="metric-card__divider" />

                        <Box className="metric-card__trend">
                          <Box className="metric-card__trend-icon">
                            {m.trend === "up" ? (
                              <svg
                                width="13"
                                height="14"
                                viewBox="0 0 13 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M6.625 0.25L11.875 5.75C12.1562 6.0625 12.1562 6.53125 11.8438 6.8125C11.5312 7.09375 11.0625 7.09375 10.7812 6.78125L6.84375 2.625V13.25C6.84375 13.6875 6.5 14 6.09375 14C5.65625 14 5.34375 13.6875 5.34375 13.25V2.625L1.375 6.78125C1.09375 7.09375 0.625 7.09375 0.3125 6.8125C0 6.53125 0 6.03125 0.28125 5.75L5.53125 0.25C5.6875 0.09375 5.875 0 6.09375 0C6.28125 0 6.46875 0.09375 6.625 0.25Z"
                                  fill="white"
                                />
                              </svg>
                            ) : (
                              <svg
                                width="13"
                                height="14"
                                viewBox="0 0 13 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M5.53125 13.7812L0.28125 8.28125C0 7.96875 0 7.5 0.3125 7.21875C0.625 6.9375 1.09375 6.9375 1.375 7.25L5.34375 11.4062V0.75C5.34375 0.34375 5.65625 0 6.09375 0C6.5 0 6.84375 0.34375 6.84375 0.75V11.4062L10.7812 7.25C11.0625 6.9375 11.5625 6.9375 11.8438 7.21875C12.1562 7.5 12.1562 7.96875 11.875 8.28125L6.625 13.7812C6.46875 13.9375 6.28125 14 6.09375 14C5.875 14 5.6875 13.9375 5.53125 13.7812Z"
                                  fill="white"
                                />
                              </svg>
                            )}
                          </Box>
                          <Typography className="metric-card__trend-text">
                            {m.vsPeers} vs peers
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* ── Bottom Row ── */}
                <Grid container spacing={2} mb={2}>
                  {/* MEI Trend */}
                  <Grid item xs={12} md={6}>
                    <Paper elevation={0} className="chart-card">
                      {/* <InfoOutlinedIcon className="chart-card__info-icon" /> */}
                      <Stack className="chart-card__header">
                        <Typography className="chart-card__title">
                          MEI Trend
                        </Typography>

                        <Box className="chart-card__header-actions">
                          <ToggleButtonGroup
                            value={trendPeriod}
                            exclusive
                            onChange={(_, v) => v && setTrendPeriod(v)}
                            className="chart-card__toggle-group"
                          >
                            <ToggleButton value="6">6 Months</ToggleButton>
                            <ToggleButton value="12">12 Months</ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                      </Stack>

                      <ResponsiveContainer width="100%" height={340}>
                        <LineChart
                          data={trendData}
                          margin={{ top: 5, right: 20, left: -20, bottom: 20 }}
                        >
                          <XAxis
                            dataKey="month"
                            label={{
                              value: "MONTHS",
                              position: "insideBottom",
                              offset: -8,
                              fontSize: 12,
                              fill: "#666666",
                              fontFamily: "DM Sans",
                              fontWeight: 700,
                            }}
                            tick={{
                              fontSize: 12,
                              fill: "#666666",
                              fontFamily: "DM Sans",
                              fontWeight: 700,
                            }}
                            axisLine={{ stroke: "#F3F4F6", strokeWidth: 1 }}
                            tickLine={false}
                          />
                          <YAxis
                            domain={[80, 120]}
                            tick={{
                              fontSize: 12,
                              fill: "#666666",
                              fontFamily: "DM Sans",
                              fontWeight: 700,
                              textAnchor: "end",
                            }}
                            axisLine={false}
                            tickLine={false}
                            label={{
                              value: "MEI SCORE",
                              angle: -90,
                              position: "insideLeft",
                              offset: 10,
                              fontSize: 12,
                              fill: "#666666",
                              fontFamily: "DM Sans",
                              fontWeight: 700,
                            }}
                          />
                          <Tooltip
                            contentStyle={{
                              borderRadius: 8,
                              border: "none",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                              fontSize: 12,
                            }}
                          />
                          <ReferenceLine
                            y={100}
                            stroke="#D9D9D9"
                            strokeWidth={5}
                          />
                          <Line
                            type="monotone"
                            dataKey="peers"
                            stroke="#D9D9D9"
                            strokeWidth={5}
                            dot={false}
                            name="Peer Median"
                          />
                          <Line
                            type="monotone"
                            dataKey="property"
                            stroke="#528A4D"
                            strokeWidth={3}
                            dot={{ fill: "#528A4D", r: 3 }}
                            activeDot={{ r: 5 }}
                            name="Your Property"
                          />
                        </LineChart>
                      </ResponsiveContainer>

                      <Box className="chart-card__legend">
                        <Box className="chart-card__legend-item">
                          <Box className="chart-card__legend-dot" />
                          <Typography className="chart-card__legend-label">
                            Peer Median
                          </Typography>
                        </Box>
                        <Box className="chart-card__legend-item">
                          <svg
                            width="16"
                            height="3"
                            viewBox="0 0 16 3"
                            fill="none"
                          >
                            <path
                              d="M1.5 1.5H13.8476"
                              stroke="#528A4D"
                              strokeWidth="3"
                              strokeLinecap="round"
                            />
                          </svg>
                          <Typography className="chart-card__legend-label">
                            Your Property
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Peer Distribution */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      className="chart-card"
                      sx={{ p: "24px 28px" }}
                    >
                      {/* <InfoOutlinedIcon className="chart-card__info-icon" /> */}
                      <Box className="chart-card__header" sx={{ mb: 2.75 }}>
                        <Typography className="distribution-card__title">
                          Where you stand vs. peer distribution
                        </Typography>
                      </Box>

                      {peerDistribution?.map((item, i) => (
                        <Box key={item.label}>
                          {i === peerDistribution?.length - 1 && (
                            <Box className="distribution-card__divider" />
                          )}
                          <Box className="distribution-card__item">
                            <Typography className="distribution-card__item-label">
                              {item.label}
                            </Typography>
                            <Box className="distribution-card__item-row">
                              <Box sx={{ flex: 1 }}>
                                <StyledProgress
                                  barcolor={item.color}
                                  variant="determinate"
                                  value={item.percentile}
                                />
                              </Box>
                              <Typography
                                className="distribution-card__percentile"
                                sx={{ color: item.color }}
                              >
                                {ordinal(item.percentile)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                </Grid>
              </>
            </Box>
          )}
        </Container>
      </PrintContainer>
    </Box>
  );
};

export default MarketingEfficiencyIndexReport;
